import { createSlice } from "@reduxjs/toolkit";
import type { Page } from "@r6digital/rapidstor-types/types/Questionnaire";
import { RootState } from "./index";
import Location from "../features/rapidstor/entities/Location";

type ResponseType = string | boolean | number;

interface State {
  location: Location | null;
  page: Page | null;
  pageStack: Page[];
  responses: Record<string, ResponseType | ResponseType[]>;
}

const initialState: State = {
  location: null,
  page: null,
  pageStack: [],
  responses: {},
};

export const questionnaireSlice = createSlice({
  name: "questionnaire",
  initialState,
  reducers: {
    setLocation: (state, action) => {
      state.location = action.payload;
    },
    resetQuestionnaire: (state) => {
      state.page = null;
      state.pageStack = [];
      state.responses = {};
    },
    goBackOnePage: (state) => {
      if (state.pageStack.length <= 1) {
        return;
      }
      state.pageStack.pop();
      state.page = state.pageStack[state.pageStack.length - 1];
    },
    changePage: (state, action) => {
      if (state.page?.id === action.payload.id) {
        return;
      }
      state.page = action.payload;
      state.pageStack.push(action.payload);
    },
    setResponse: (state, action) => {
      const { id, response } = action.payload;
      state.responses[id] = response;
    },
    deleteResponse: (state, action) => {
      delete state.responses[action.payload];
    },
    addResponseArray: (state, action) => {
      const { id, response } = action.payload;
      if (!state.responses[id]) {
        state.responses[id] = [] as ResponseType[];
      }
      if (Array.isArray(state.responses[id])) {
        (state.responses[id] as ResponseType[]).push(response);
      }
    },
    deleteResponseArray: (state, action) => {
      const { id, response } = action.payload;
      if (Array.isArray(state.responses[id])) {
        (state.responses[id] as ResponseType[]).splice(
          (state.responses[id] as ResponseType[]).indexOf(response),
          1
        );
      }
    },
  },
});

export const {
  setLocation,
  resetQuestionnaire,
  goBackOnePage,
  changePage,
  setResponse,
  deleteResponse,
  addResponseArray,
  deleteResponseArray,
} = questionnaireSlice.actions;

export const selectLocation = (state: RootState) => state.questionnaire.location;
export const selectPage = (state: RootState) => state.questionnaire.page;
export const selectPageStack = (state: RootState) => state.questionnaire.pageStack;
export const selectResponses = (state: RootState) => state.questionnaire.responses;
